import React, { createContext, useContext, useEffect, useState } from 'react';

import { errorLogger, ErrorLogEntry } from '../../utils/errorLogger';

interface ErrorReportingContextType {
  errorLogs: ErrorLogEntry[];
  errorSummary: {
    total: number;
    bySeverity: Record<string, number>;
    recent: ErrorLogEntry[];
  };
  clearLogs: () => void;
  refreshLogs: () => void;
}

const ErrorReportingContext = createContext<
  ErrorReportingContextType | undefined
>(undefined);

interface ErrorReportingProviderProps {
  children: React.ReactNode;
}

export const ErrorReportingProvider: React.FC<ErrorReportingProviderProps> = ({
  children,
}) => {
  const [errorLogs, setErrorLogs] = useState<ErrorLogEntry[]>([]);
  const [errorSummary, setErrorSummary] = useState<{
    total: number;
    bySeverity: Record<string, number>;
    recent: ErrorLogEntry[];
  }>({
    total: 0,
    bySeverity: {},
    recent: [],
  });

  const refreshLogs = () => {
    setErrorLogs(errorLogger.getErrorLogs());
    setErrorSummary(errorLogger.getErrorSummary());
  };

  const clearLogs = () => {
    errorLogger.clearErrorLogs();
    refreshLogs();
  };

  useEffect(() => {
    refreshLogs();

    // Refresh logs every 30 seconds
    const interval = setInterval(refreshLogs, 30000);

    return () => clearInterval(interval);
  }, []);

  const contextValue: ErrorReportingContextType = {
    errorLogs,
    errorSummary,
    clearLogs,
    refreshLogs,
  };

  return (
    <ErrorReportingContext.Provider value={contextValue}>
      {children}
    </ErrorReportingContext.Provider>
  );
};

export const useErrorReporting = (): ErrorReportingContextType => {
  const context = useContext(ErrorReportingContext);
  if (!context) {
    throw new Error(
      'useErrorReporting must be used within ErrorReportingProvider'
    );
  }
  return context;
};
